<template>
    <form
        class="base-show-information-form"
        novalidate
        @submit.prevent="onSubmit"
    >
        <form-row
            id="title"
            class="show-title"
            label="Podcast Title"
            :error="fields.title.error"
        >
            <form-input
                id="title"
                v-model="fields.title.value"
                type="text"
                autocomplete="off"
                :error="!! fields.title.error"
                @focus="clearError('title')"
            />
        </form-row>

        <form-row
            id="description"
            class="show-description"
            label="Podcast Description"
            :help="descriptionRemainingCharactersHelp"
            :error="fields.description.error"
        >
            <form-rich-editor
                id="description"
                v-model="fields.description.value"
                name="description"
                :error="!! fields.description.error"
                @focus="clearError('description')"
            />
        </form-row>

        <form-row
            id="artwork"
            class="show-artwork"
            label="Episode Artwork"
            help="(JPG or PNG, square image between 1400x1400 and 3000x3000 pixels)"
            :error="fields.artwork.error"
        >
            <form-image
                id="artwork"
                ref="customArtwork"
                :accept="artworkAllowedExtensions"
                select-label="Upload artwork"
                change-label="Change artwork"
                :preview-url="artworkPreview"
                :error="!! fields.artwork.error"
                @change="onArtworkChange"
            />
        </form-row>

        <base-form-button
            class="btn next-step-button"
            :has-arrow="true"
            :loading="isSubmitting"
        >
            Let's Get Started
        </base-form-button>
    </form>
</template>
<script>
import { find, mapValues, uniq } from 'lodash';
import { mapState } from 'vuex';
import FormRow from '@/components/Form/FormRow';
import FormInput from '@/components/Form/FormInput';
import FormRichEditor from '@/components/Form/FormRichEditor';
import FormImage from '@/components/Form/FormImage';
import toasts from '@/mixins/toasts';
import apiCall from '@/utils/apiCall';
import {
    validateTitle,
    validateDescription,
    validateArtwork,
} from '@/utils/form/showValidation';
import { convertToFormData } from '@/utils/formData';
import htmlToText from '@/utils/htmlToText';
import {
    descriptionCharactersLimit,
    artworkAllowedTypes,
} from '@/utils/config/showOptions';

export default {
    components: {
        FormImage,
        FormInput,
        FormRichEditor,
        FormRow,
    },

    mixins: [
        toasts,
    ],

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        isSubmitting: false,
        artworkPreview: null,
        fields: {
            title: {
                error: false,
                value: '',
            },
            description: {
                error: false,
                value: '',
            },
            artwork: {
                error: false,
                value: null,
            },
        },
    }),

    computed: {
        ...mapState(['user']),

        /**
         * Computes amount of remaining characters in Description.
         *
         * @returns {number} Amount of remaining characters.
         */
        descriptionRemainingCharacters() {
            return descriptionCharactersLimit - htmlToText(this.fields.description.value || '').length;
        },

        /**
         * Computes help text for description remaining characters.
         *
         * @returns {string} Description remainig charaters help text.
         */
        descriptionRemainingCharactersHelp() {
            if (0 <= this.descriptionRemainingCharacters) {
                return `(${this.descriptionRemainingCharacters} character(s) remaining)`;
            }

            return `(over ${-this.descriptionRemainingCharacters} characters)`;
        },

        /**
         * Returns list of allowed Artwork extensions.
         *
         * @returns {string[]} List of allowed Artwork extensions.
         */
        artworkAllowedExtensions() {
            return Object.keys(artworkAllowedTypes);
        },

        /**
         * Returns list of allowed Artwork MIME types.
         *
         * @returns {string[]} List of allowed Artwork MIME types.
         */
        artworkAllowedMimeTypes() {
            return uniq(Object.values(artworkAllowedTypes));
        },
    },

    /**
     * Fills form with Show values.
     */
    mounted() {
        this.fields.title.value = this.show?.title || '';
        this.fields.description.value = this.show?.description || '';

        this.artworkPreview = this.show?.cover?.imageUrl || null;
    },

    methods: {
        /**
         * Clears fields error.
         *
         * @param {string} field Field name.
         */
        clearError(field) {
            this.fields[field].error = false;
        },

        /**
         * Changes value of artwork.
         *
         * @param {File} artwork Selected artwork.
         */
        onArtworkChange(artwork) {
            this.clearError('artwork');
            this.fields.artwork.value = artwork;
        },

        /**
         * Submits form.
         */
        async onSubmit() {
            if (! await this.validate()) {
                this.toastError({ message: { title: 'Please resolve errors in form.' } });

                return;
            }

            this.isSubmitting = true;

            const { status, data } = await this.updateOrCreateShow();

            if (! [200, 201].includes(status)) {
                this.toastError({
                    message: { title: data.errors },
                });

                return;
            }

            this.isSubmitting = false;

            // `data` format is legacy
            this.$emit('submit', data.data || data);
        },

        /**
         * Validates form data.
         *
         * @returns {boolean} Validation has errors or not.
         */
        async validate() {
            this.fields.title.error = validateTitle(this.fields.title.value);
            this.fields.description.error = validateDescription(this.fields.description.value, {
                charactersLimit: descriptionCharactersLimit,
            });
            this.fields.artwork.error = await validateArtwork(this.fields.artwork.value, {
                allowedMimeTypes: this.artworkAllowedMimeTypes,
            });

            return ! find(this.fields, (field) => !! field.error);
        },

        /**
         * Updates or creates show based on current state.
         *
         * @returns {Promise} API call promise.
         */
        updateOrCreateShow() {
            const data = convertToFormData(
                mapValues(this.fields, 'value'),
                { skipEmpty: true }
            );

            return this.show?.hash ? this.updateShow(data) : this.createShow(data);
        },

        /**
         * Updates show.
         *
         * @param {FormData} formData Form data.
         * @returns {Promise} API call promise.
         */
        updateShow(formData) {
            formData.append('hosted', 1);

            return apiCall(this, 'put', `/api/shows/${this.show.hash}`, formData);
        },

        /**
         * Creates show.
         *
         * @param {FormData} formData Form data.
         * @returns {Promise} API call promise.
         */
        createShow(formData) {
            return apiCall(this, 'post', '/api/shows', formData);
        },
    },
};
</script>
